import React from 'react';
import { Backdrop, Grow, Paper, Popper, PopperProps, Typography, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ICSEvent } from '@uplift/api';
import { CalendarDateService, formatTimeRangeWithMeridiem } from '@uplift/services';
import { useTimezone } from '@uplift/contexts';
import { useQuery } from '@tanstack/react-query';
import { ModalContent, ModalTitle } from '../../Modal';
import { DateFormats } from '@uplift/constants';


const SimplePopover: React.FC<{
  anchorEl?: HTMLElement;
  onClose: () => void;
  placement: PopperProps['placement'];
}> = ({ anchorEl, onClose, placement, children }) => {
  const theme = useTheme();

  return (
    <>
      <Backdrop open={!!anchorEl} invisible onClick={onClose} style={{ zIndex: theme.zIndex.modal }} />

      <Popper
        keepMounted
        open={!!anchorEl}
        anchorEl={anchorEl}
        modifiers={[
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              padding: 8,
              boundariesElement: 'viewport',
            },
          },
          {
            name: 'offset',
            options: {
              offset: [ 0, 3 ]
            }
          }
        ]}
        popperOptions={{
          strategy: 'fixed',
        }}
        placement={placement}
        style={{ zIndex: theme.zIndex.modal }}
      >
        <Grow in={!!anchorEl}>
          <Paper elevation={20}>
            {children}
          </Paper>
        </Grow>
      </Popper>
    </>
  );
};


export const ICSEventDetails: React.VFC<{
  event: ICSEvent;
}> = ({ event }) => {
  const timezone = useTimezone();
  const time = React.useMemo(() => {
    if (!event.fromDatetime || !event.toDatetime) {
      return null;
    }

    const fromDatetime = event.allDay
      ? CalendarDateService.date.tz(event.fromDatetime, DateFormats.ISO_DATE, timezone)
      : CalendarDateService.date(event.fromDatetime).tz(timezone);
    const toDatetime = event.allDay
      ? CalendarDateService.date.tz(event.toDatetime, DateFormats.ISO_DATE, timezone)
      : CalendarDateService.date(event.toDatetime).tz(timezone);

    if (event.allDay) {
      return `${fromDatetime.format('ddd, MMM D')}`;
    } else {
      return `${fromDatetime.format('ddd, MMM D')} at ${formatTimeRangeWithMeridiem(fromDatetime, toDatetime, { forceMinutes: false })}`;
    }
  }, [ event, timezone ]);

  const { data: recurringLabel } = useQuery({
    queryKey: [ 'rruleRepresentation', event.rrule ],
    queryFn: async () => {
      if (!event.rrule) {
        return;
      }

      const rrule = await import('rrule');
      const humanText = rrule.rrulestr(event.rrule).toText();

      if (!humanText) {
        return null;
      }

      return `${humanText.charAt(0).toUpperCase()}${humanText.substring(1)}`;
    }
  });

  return (
    <>
      <Typography>{time}</Typography>
      {recurringLabel && <Typography color="textSecondary">{recurringLabel}</Typography>}
      <Typography>{event.description}</Typography>
    </>
  );
};


const useStyles = makeStyles()({
  title: {
    fontSize: '18px !important',
  }
});


export const ICSEventDetailsPopup: React.FC<{
  anchorEl?: HTMLElement;
  onClose: () => void;
  event?: ICSEvent;
}> = ({
  anchorEl,
  onClose,
  event,
}) => {
  const { classes } = useStyles();

  if (anchorEl && event) {
    return (
      <SimplePopover
        placement="right-start"
        anchorEl={anchorEl}
        onClose={onClose}
      >
        <div style={{ width: '420px' }}>
          <ModalTitle classes={{ title: classes.title }} onClose={onClose}>
            {event.title}
          </ModalTitle>
          <ModalContent dividers>
            <ICSEventDetails
              event={event}
            />
          </ModalContent>
        </div>
      </SimplePopover>
    );
  }

  return null;
};
